import { createContext, useContext, useMemo, useRef, type ReactNode } from "react";

type Registry = {
  register: (key: string) => () => void;
};

const noopRegistry: Registry = {
  register: () => () => {},
};

const DialogRegistryContext = createContext<Registry>(noopRegistry);

export function DialogRegistryProvider({ children }: { children: ReactNode }) {
  const counts = useRef(new Map<string, number>());

  const value = useMemo<Registry>(
    () => ({
      register: (key: string) => {
        const map = counts.current;
        const next = (map.get(key) ?? 0) + 1;
        map.set(key, next);
        if (next > 1) {
          console.error(
            `[useDialog] Two components are subscribed to the same dialog key "${key}". ` +
              `Use a namespaced key like "feature:action" (e.g. "projects:create") so URL ` +
              `state for one dialog doesn't accidentally open another.`,
          );
        }
        return () => {
          const current = map.get(key);
          if (current === undefined) return;
          if (current <= 1) map.delete(key);
          else map.set(key, current - 1);
        };
      },
    }),
    [],
  );

  return (
    <DialogRegistryContext.Provider value={value}>
      {children}
    </DialogRegistryContext.Provider>
  );
}

export function useDialogRegistry(): Registry {
  return useContext(DialogRegistryContext);
}
